import { Input } from "./Input"
import { Fragment, type ReactNode, useEffect, useId, useRef, useState } from "react"

import { contaGuardada } from "../api"
import { MAIUSCULA, ROTULO_DE_CAMPO } from "../lib/classes"
import { cn } from "../lib/cn"
import { mensagemDeRecusa } from "./BlocoDeCampos"
import { Button } from "./Button"
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./Dialog"
import * as Icones from "./Icones"
import { RotuloDoCampo } from "./RotuloDoCampo"

const APOIO = "mt-1 block text-(length:--texto-xs) text-(--tinta-3)"
const FAIXA_CRITICA =
  "rounded-(--raio-controle) border border-(--vermelho) bg-(--vermelho-fundo) px-[15px] py-[13px] text-[0.8125rem] [&_p]:mt-1.5"
const CANCELAR = "border-destructive text-destructive hover:text-destructive"

export function ResumoDoAto({ itens }: { itens: [rotulo: string, valor: ReactNode][] }) {
  return (
    <dl
      data-slot="resumo-do-ato"
      className={cn(
        "grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-1 rounded-(--raio-controle) border border-(--borda) bg-(--fundo) p-3 [&>dd]:m-0 [&>dd]:flex [&>dd]:min-w-0 [&>dd]:items-center [&>dd]:gap-2 [&>dd]:break-words [&>dt]:text-(length:--texto-xs) [&>dt]:tracking-(--tracking-rotulo) [&>dt]:text-(--tinta-3) [&>dt]:uppercase",
        "[:root:not([data-tema=escuro])_&]:border-(--azul-destaque) [:root:not([data-tema=escuro])_&]:bg-(--marca-tinta-2) [:root:not([data-tema=escuro])_&>dt]:text-(--azul-800) [:root:not([data-tema=escuro])_&>dd]:text-(--tinta)",
      )}
    >
      {itens.map(([rotulo, valor]) => (
        <Fragment key={rotulo}>
          <dt>{rotulo}</dt>
          <dd>{valor}</dd>
        </Fragment>
      ))}
    </dl>
  )
}

export function ConfirmarComSenha({
  aberto,
  titulo,
  resumo,
  rotuloDoAto,
  iconeDoAto,
  tom = "azul",
  classeDoAto,
  rotuloDaConta = "Sua conta:",
  explicacao = "Sua senha confirma que é você quem faz este registro agora. Ele não pode ser desfeito.",
  cpf,
  aoConfirmar,
  aoFechar,
  aoDevolverFoco,
}: {
  aberto: boolean
  titulo: string
  resumo?: ReactNode
  rotuloDoAto: string
  iconeDoAto?: ReactNode
  tom?: "azul" | "perigo"
  classeDoAto?: string
  rotuloDaConta?: string
  explicacao?: string
  cpf?: string
  aoConfirmar: (senha: string) => Promise<unknown>
  aoFechar: () => void
  aoDevolverFoco?: () => void
}) {
  const [senha, definirSenha] = useState("")
  const [gravando, definirGravando] = useState(false)
  const [recusa, definirRecusa] = useState<string | null>(null)
  const campoDaSenha = useRef<HTMLInputElement>(null)
  const idDaSenha = useId()
  const conta = cpf ?? contaGuardada()?.cpf ?? ""

  useEffect(() => {
    if (!aberto) return
    definirSenha("")
    definirRecusa(null)
    definirGravando(false)
  }, [aberto])

  const confirmar = () => {
    if (senha === "" || gravando) return
    definirGravando(true)
    definirRecusa(null)
    aoConfirmar(senha)
      .then(() => {
        definirGravando(false)
        aoFechar()
      })
      .catch((erro: unknown) => {
        definirGravando(false)
        definirSenha("")
        definirRecusa(mensagemDeRecusa(erro))
        campoDaSenha.current?.focus()
      })
  }

  return (
    <Dialog
      open={aberto}
      onOpenChange={(abrir) => {
        if (!abrir && !gravando) aoFechar()
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        className="sm:max-w-[30rem]"
        onOpenAutoFocus={(evento) => {
          evento.preventDefault()
          campoDaSenha.current?.focus()
        }}
        onCloseAutoFocus={(evento) => {
          if (aoDevolverFoco === undefined) return
          evento.preventDefault()
          aoDevolverFoco()
        }}
      >
        <form
          className="flex min-h-0 flex-col"
          noValidate
          onSubmit={(evento) => {
            evento.preventDefault()
            confirmar()
          }}
        >
          <DialogHeader tom={tom}>
            <DialogTitle>{titulo}</DialogTitle>
          </DialogHeader>

          <DialogBody className="flex flex-col gap-4">
            {resumo}

            <div>
              <label className="block">
                <span className={ROTULO_DE_CAMPO}>{rotuloDaConta}</span>
                <Input className="bg-(--fundo) text-(--tinta-2)"
                  type="text"
                  name="cpf"
                  autoComplete="username"
                  readOnly
                  value={conta}
                />
              </label>

              <div className="mt-3">
                <RotuloDoCampo htmlFor={idDaSenha} obrigatorio>
                  Sua senha
                </RotuloDoCampo>
                <Input
                  ref={campoDaSenha}
                  id={idDaSenha}
                  type="password"
                  required
                  aria-required="true"
                  name="senha"
                  autoComplete="current-password"
                  value={senha}
                  onChange={(evento) => definirSenha(evento.target.value)}
                />
                <span className={APOIO}>{explicacao}</span>
              </div>
            </div>

            {recusa !== null && (
              <div className={FAIXA_CRITICA} role="alert">
                <strong>Não foi possível registrar</strong>
                <p className={MAIUSCULA}>{recusa}</p>
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={CANCELAR}
              onClick={aoFechar}
              disabled={gravando}
            >
              <Icones.Fechar />
              Cancelar
            </Button>
            <Button
              title={senha === "" ? "Falta a sua senha" : undefined}
              type="submit"
              size="sm"
              variant={tom === "perigo" && classeDoAto === undefined ? "destructive" : "default"}
              className={classeDoAto}
              disabled={senha === "" || gravando}
            >
              {iconeDoAto}
              {gravando ? "Registrando..." : rotuloDoAto}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
